/** Spanish for the core area (docs/conventions/i18n.md). Keys are the English copy, verbatim. */
export const esCore: Readonly<Record<string, string>> = {
  'just now': 'ahora',
  '{count} min ago': 'hace {count} min',
  '{count} h ago': 'hace {count} h',

  // App shell and navigation
  'Survival kit': 'Kit de supervivencia',
  Sections: 'Secciones',
  More: 'Más',
  Dashboard: 'Panel',
  Inventory: 'Inventario',
  Shopping: 'Compras',
  Tasks: 'Tareas',
  Documents: 'Documentos',
  Notes: 'Notas',
  Trash: 'Papelera',
  'A new version is available': 'Hay una versión nueva',
  Update: 'Actualizar',
  'Ready to use offline': 'Lista para usarse sin conexión',
  Records: 'Archivo',
  Data: 'Datos',
  Sessions: 'Sesiones',
  Breadcrumb: 'Ruta de navegación',
  'Toggle the sidebar': 'Mostrar u ocultar la barra lateral',
  'Toggle the sidebar (⌘B)': 'Mostrar u ocultar la barra lateral (⌘B)',
  'Sync, storage on this device and the trash.':
    'Sincronización, almacenamiento en este dispositivo y papelera.',
  Storage: 'Almacenamiento',
  Filters: 'Filtros',
  'Filters ({count} on)': 'Filtros ({count} activos)',
  'Clear filters': 'Quitar filtros',
  'Show results': 'Ver resultados',
  'Moved to the trash': 'Movido a la papelera',
  Undo: 'Deshacer',

  // Shared components
  Cancel: 'Cancelar',
  Confirm: 'Confirmar',
  Delete: 'Eliminar',
  Save: 'Guardar',
  Loading: 'Cargando',
  'backpack, car, winter': 'mochila, coche, invierno',
  'Separated by commas': 'Separadas por comas',

  // Auth
  Account: 'Cuenta',
  'Sign out on this device?': '¿Cerrar sesión en este dispositivo?',
  '{count} change has not reached the server and will be lost. Sync first if you can.':
    'Hay {count} cambio sin enviar al servidor y se perderá. Sincroniza antes si puedes.',
  '{count} changes have not reached the server and will be lost. Sync first if you can.':
    'Hay {count} cambios sin enviar al servidor y se perderán. Sincroniza antes si puedes.',
  "This device's local copy will be erased. The data stays on the server.":
    'Se borrará la copia local de este dispositivo. Los datos siguen en el servidor.',
  'Sign out': 'Cerrar sesión',
  'Version {version} · server {server}': 'Versión {version} · servidor {server}',
  'Version {version}': 'Versión {version}',
  'Your session has expired. Sign in again to keep syncing.':
    'Tu sesión ha caducado. Entra de nuevo para seguir sincronizando.',
  'Sign in with your account.': 'Entra con tu cuenta.',
  'Email or username': 'Email o usuario',
  Password: 'Contraseña',
  Offline: 'Sin conexión',
  'Sign in': 'Entrar',
  'Cannot reach the server. Signing in for the first time needs a connection.':
    'No se puede conectar con el servidor. Para entrar la primera vez hace falta conexión.',
  'Wrong email, username or password.': 'Email, usuario o contraseña incorrectos.',
  'Your session has expired. Your changes are still saved on this device.':
    'La sesión ha caducado. Tus cambios siguen guardados en este dispositivo.',

  // Sync status
  '{count} change refused by the server': '{count} cambio rechazado por el servidor',
  '{count} changes refused by the server': '{count} cambios rechazados por el servidor',
  'the record had been deleted': 'el registro se había eliminado',
  'Got it, clear the notice': 'Entendido, borrar el aviso',
  Sync: 'Sincronización',
  'The server is the source of truth; this device keeps a full copy to work offline.':
    'El servidor es la fuente de la verdad; este dispositivo guarda una copia completa para trabajar sin conexión.',
  'Sync now': 'Sincronizar ahora',
  'Last synced: {time}.': 'Última sincronización: {time}.',
  'Not synced yet.': 'Aún sin sincronizar.',
  '{count} change to send.': '{count} cambio por enviar.',
  '{count} changes to send.': '{count} cambios por enviar.',
  'Syncing…': 'Sincronizando…',
  'Offline · {count}': 'Sin conexión · {count}',
  'Everything is saved on this device and will be sent when the connection is back.':
    'Todo se guarda en este dispositivo y se enviará al volver la conexión.',
  'Sign in again': 'Vuelve a entrar',
  'Your session has expired.': 'La sesión ha caducado.',
  'Your changes are safe on this device.': 'Tus cambios están a salvo en este dispositivo.',
  'Sync error': 'Error de sincronización',
  '{count} to send': '{count} por enviar',
  Synced: 'Sincronizado',
  'A change of yours was not applied: someone had deleted the record. You can restore it from the trash.':
    'Un cambio tuyo no se aplicó: alguien había eliminado el registro. Puedes restaurarlo desde la papelera.',
  'The server refused a change: {message}': 'El servidor rechazó un cambio: {message}',

  // Settings
  Theme: 'Tema',
  Language: 'Idioma',
  'Same as the system': 'Como el sistema',
  'Same as the browser': 'Como el navegador',
  Light: 'Claro',
  Dark: 'Oscuro',
  'Persistent storage is on': 'Almacenamiento persistente activado',
  'The browser has not granted it yet': 'El navegador no lo ha concedido todavía',
  'This device': 'Este dispositivo',
  'So the browser never erases the local copy when space runs low, ask for persistent storage. On iPhone, add the app to the home screen.':
    'Para que el navegador no borre la copia local si falta espacio, pide almacenamiento persistente. En iPhone, añade la app a la pantalla de inicio.',
  'Storage is not persistent': 'Almacenamiento no persistente',
  '{size} used': '{size} usados',
  'Ask for persistence': 'Pedir persistencia',
  '{count} file on this device ({size})': '{count} archivo en este dispositivo ({size})',
  '{count} files on this device ({size})': '{count} archivos en este dispositivo ({size})',
  '{count} to upload': '{count} por subir',
  'Export data (JSON)': 'Exportar datos (JSON)',

  // Trash
  Untitled: 'Sin título',
  Restored: 'Restaurado',
  'The trash is empty': 'La papelera está vacía',
  '{collection} · deleted by {person} · {time}': '{collection} · eliminado por {person} · {time}',
  Restore: 'Restaurar',

  // Sessions and access
  'Sessions and access': 'Sesiones y accesos',
  'Approve the agents that ask you and revoke what you no longer use.':
    'Aprueba a los agentes que te lo pidan y revoca lo que ya no uses.',
  'Requested {ago} · expires in {minutes} min': 'Pedido {ago} · caduca en {minutes} min',
  Deny: 'Rechazar',
  'Give {name} access?': '¿Dar acceso a {name}?',
  'It will be able to see and change the whole kit in your name for 30 days, until you revoke it. Check that the code {code} is the same one it shows you.':
    'Podrá ver y cambiar todo el kit en tu nombre durante 30 días, hasta que lo revoques. Comprueba que el código {code} es el mismo que te muestra.',
  Allow: 'Permitir',
  Agent: 'Agente',
  'Unnamed device': 'Dispositivo sin nombre',
  'Last used {ago} · expires {date}': 'Último uso {ago} · caduca {date}',
  "Revoke {name}'s access?": '¿Revocar el acceso de {name}?',
  "Revoke {name}'s session?": '¿Revocar la sesión de {name}?',
  'It will no longer see or change the kit in your name. What it already did stays.':
    'Dejará de poder ver o cambiar el kit en tu nombre. Lo que ya hizo se queda.',
  'That device will have to sign in again. Its unsent changes stay on it until then.':
    'Ese dispositivo tendrá que volver a entrar. Sus cambios sin enviar se quedan en él hasta entonces.',
  Revoke: 'Revocar',
  'Revoke {name}': 'Revocar {name}',
  'Offline: access and sessions can only be managed with a connection.':
    'Sin conexión: los accesos y las sesiones solo se gestionan con conexión.',
  'They could not be loaded. Try again.': 'No se han podido cargar. Inténtalo de nuevo.',
  '{name} now has access': '{name} ya tiene acceso',
  'Request denied': 'Solicitud rechazada',
  'The request is no longer pending (it may have expired)':
    'La solicitud ya no está pendiente (puede haber caducado)',
  'Access revoked': 'Acceso revocado',
  'It could not be revoked. Try again.': 'No se ha podido revocar. Inténtalo de nuevo.',
  'No data': 'Sin datos',
  'Access requests': 'Solicitudes de acceso',
  'There is no pending request with the code {code}. It may have expired (they last 10 minutes): ask the agent to start another one.':
    'No hay ninguna solicitud pendiente con el código {code}. Puede haber caducado (duran 10 minutos): pide al agente que empiece otra.',
  'No pending requests': 'No hay solicitudes pendientes',
  'An agent (Claude, a script…) can ask for access by following {url}. Its request will show up here with a code.':
    'Un agente (Claude, un script…) puede pedir acceso siguiendo {url}. Su solicitud aparecerá aquí con un código.',
  'Agents with access': 'Agentes con acceso',
  'No agent has access': 'Ningún agente tiene acceso',
  'Your devices': 'Tus dispositivos',
};
